import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { UtilService } from '@services/util.service';
import { LoginService } from '@services/login.service';
import { LogService } from '@services/log.service';
import { PreferencesService } from '@services/preferences.service';
import { LoginType } from '@models/registration.models';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    RouterModule,
    CommonModule
  ],
})
export class SignInComponent {
  preferenceService = inject(PreferencesService);
  authForm!: FormGroup;
  isRegInProgress = false;
  queryParams: Record<string, string> = {};
  
  // Auth method configuration
  showMagicLink = false;
  showOTP = false;
  
  // OTP state
  otpSent = false;
  encryptedData: string | null = null;

  isSdkFlow = false;

  // Carried into the login link/OTP so the post-login landing is /meter-details?consentRequestId=...
  // Kept in a field because handleAutoOTPFlow clears the URL params.
  private consentRequestId: string | null = null;

  constructor(
    private fb: FormBuilder, 
    private utilService: UtilService, 
    private loginService: LoginService,
    private logService: LogService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Log page view
    this.logService.logPageView('sign-in', {
      queryParams: this.route.snapshot.queryParams
    });

    this.authForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });

    const loginType = this.preferenceService.getLoginType();
    if (loginType === LoginType.EMAIL_MAGIC_LINK) {
      this.showMagicLink = true;
    } else if (loginType === LoginType.EMAIL_OTP) {
      this.showOTP = true;
    } else if (loginType === LoginType.EMAIL_LINK_AND_OTP) {
      this.showMagicLink = true;
      this.showOTP = true;
    }
    
    // Store current query parameters and configure auth methods
    this.route.queryParams.subscribe(params => {
      this.queryParams = { ...params };
      if (params['consentRequestId']) {
        this.consentRequestId = params['consentRequestId'];
      }
      if (params['email'] && !params['encryptedData']) {
        // Sent here from /register (or elsewhere) for an existing user - just prefill
        this.authForm.get('email')?.setValue(params['email']);
      }
      this.handleAutoOTPFlow(params);
    });
  }

  private handleAutoOTPFlow(params: any): void {
    const email = params['email'];
    const encryptedData = params['encryptedData'];
    this.isSdkFlow = params['isSdkFlow'] === 'true' || params['isSdkFlow'] === true;

    if (email && encryptedData) {
      this.encryptedData = encryptedData;
      // Pre-fill email and automatically send OTP
      this.authForm.get('email')?.setValue(email);
      this.otpSent = true;
      
      // Show success message about registration
      this.utilService.openSnackBar('Registration successful! An OTP has been sent to your email for login.', 'Close', 'success');
      
      // Clear the query parameters to prevent re-triggering
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true
      });
    }
  }

  onSubmit(): void {
    if (this.authForm.valid && this.otpSent && this.encryptedData) {
      // Verify OTP
      this.isRegInProgress = true;
      console.log('Verifying OTP:', this.authForm.value.otp, 'for email:', this.authForm.value.email);
      
      // Log OTP verification attempt
      this.logService.addLoginLog(this.authForm.value.email, 'VERIFY_OTP');
      
      this.loginService.verifyOTP(this.authForm.value.otp, this.encryptedData).subscribe({
        next: (magicLink: string) => {
          this.isRegInProgress = false;
          console.log('OTP verification successful, received magic link');
          this.utilService.openSnackBar('OTP verified successfully! Logging you in...', 'Close', 'success');
          
          // Navigate directly to the magic link URL
          window.location.href = magicLink;
        },
        error: (error: Error) => {
          this.isRegInProgress = false;
          console.error('OTP verification failed:', error);
          
          if (error.message === 'INVALID_OTP') {
            this.utilService.openSnackBar('Invalid OTP. Please check and try again.', 'Close', 'error');
          } else if (error.message === 'OTP_EXPIRED') {
            this.utilService.openSnackBar('OTP has expired. Please request a new one.', 'Close', 'error');
            this.otpSent = false;
            this.encryptedData = null;
          } else {
            this.utilService.openSnackBar('OTP verification failed. Please try again.', 'Close', 'error');
          }
        }
      });
    }
  }

  sendMagicLink(): void {
    if (this.authForm.get('email')?.valid) {
      this.isRegInProgress = true;
      console.log('Sending Magic Link to:', this.authForm.value.email);
      
      // Log magic link send attempt
      this.logService.addLoginLog(this.authForm.value.email, 'MAGIC_LINK');
      
      this.loginService.sendMagicLink(this.authForm.value.email, environment.magicLinkRedirectUrl, this.loginCustomParams()).subscribe({
        next: () =>  {
          this.utilService.openSnackBar('A mail was sent to your email address. Follow the instructions in the mail to log-in.', 'Close', 'success');
          this.isRegInProgress = false;
          this.authForm.get('email')?.reset();
        },
        error: (err: any) => {
          this.isRegInProgress = false;
          if (err?.error?.errorDetail === 'User does not exist.') {
            // Even for a non-existent user, we show the same message as for a successful login
            this.utilService.openSnackBar('A mail was sent to your email address. Follow the instructions in the mail to log-in.', 'Close', 'success');  
          } else if (err?.error?.error) {
            this.utilService.openSnackBar(err.error.error, 'Close', 'error')
          } else {
            this.utilService.openSnackBar('An error occurred. Please try again later.', 'Close', 'error')
          }
        }
      });
    }
  }

  private sendOTPRequest(isResend: boolean = false): void {
    if (this.authForm.get('email')?.valid) {
      this.isRegInProgress = true;
      const action = isResend ? 'Resending' : 'Sending';
      console.log(`${action} OTP to:`, this.authForm.value.email);
      
      // Log OTP send/resend attempt
      this.logService.addLoginLog(this.authForm.value.email, isResend ? 'RESEND_OTP' : 'SEND_OTP');
      
      const magicLink = this.isSdkFlow ? environment.magicLinkSdk : environment.magicLinkRedirectUrl;

      this.loginService.sendOTP(this.authForm.value.email, magicLink, this.loginCustomParams()).subscribe({
        next: (encryptedData: string | null) => {
          this.isRegInProgress = false;
          if (encryptedData) {
            this.encryptedData = encryptedData;
            if (!isResend) {
              this.otpSent = true;
            }
            const message = isResend ? '6-digit code resent to your email address' : '6-digit code sent to your email address';
            this.utilService.openSnackBar(message, 'Close', 'success');
          } else {
            const message = isResend ? 'Failed to resend 6-digit code. Please try again.' : 'Failed to send 6-digit code. Please try again.';
            this.utilService.openSnackBar(message, 'Close', 'error');
          }
        },
        error: (error: any) => {
          this.isRegInProgress = false;
          console.error(`Error ${isResend ? 'resending' : 'sending'} OTP:`, error);
          if (error?.error?.errorDetail === 'User does not exist.') {
            // Even for a non-existent user, we show the same message as for a successful send
            const message = isResend ? '6-digit code resent to your email address' : '6-digit code sent to your email address';
            this.utilService.openSnackBar(message, 'Close', 'success');
          } else if (error?.error?.error) {
            this.utilService.openSnackBar(error.error.error, 'Close', 'error');
          } else {
            const message = isResend ? 'Failed to resend 6-digit code. Please try again later.' : 'Failed to send 6-digit code. Please try again later.';
            this.utilService.openSnackBar(message, 'Close', 'error');
          }
        }
      });
    }
  }

  private loginCustomParams(): { consentRequestId: string } | undefined {
    return this.consentRequestId ? { consentRequestId: this.consentRequestId } : undefined;
  }

  sendOTP(): void {
    this.sendOTPRequest(false);
  }

  resendOTP(): void {
    this.sendOTPRequest(true);
  }

  goBack(): void {
    this.otpSent = false;
    this.encryptedData = null;
    this.authForm.get('otp')?.reset();
  }

}
